import * as React from 'react';
import GoogleMapReact from 'google-map-react';
import { connect } from 'react-redux';
import { Button } from '@material-ui/core';

import { formatShortPrice, makeId, normalizePrice } from '../../../lib/helpers';
import config from '../../../services/config';
import { isWeb, isMobile } from '../../../lib/getDeviceSize';
import EventEmitter from '../../../lib/eventEmitter';
import { Pencil, X } from 'phosphor-react';
import {
  DarkBlueMarker,
  BlueMarker,
  GreenMarker,
  FavoriteMarker,
} from '../Icons';
import CustomMarker from './CustomMarker';
import ClusterMarker from './ClusterMarker';
import SaveSearchButton from '../../pages/search/SaveSearchButton';
import styles from '../../../styles/sass/pages/Search.module.scss';
import { FEED_FETCH_LIMIT } from '../../../actions/feed';

export interface Props {
  allowDrawing?: boolean;
  center: any;
  defaultMarker?: any;
  zoom: number;
  favorites?: any[];
  feed: any[];
  feedFetched: boolean;
  focusMarker: Function;
  groupedResults: any;
  initialMapLoad?: boolean;
  isOverlayShown: boolean;
  loadMapFeed?: Function;
  markerClicked: string;
  markerFocused: string;
  markerReset: Function;
  onMarkerClick: Function;
  onMarkerEnter: Function;
  onMarkerLeave: Function;
  overlayListing: any;
  overlayText: string;
  placeBoundaries?: any;
  router: any;
  selectedListing: any;
  toggleCityBoundary?: Function;
}

interface State {
  center: any;
  isDrawable: boolean;
  mapCleared: boolean;
  mapReady: boolean;
  polygonCoordinates: string;
  showMarkers: boolean;
}

export class GoogleMapContainer extends React.Component<Props, State> {
  mapView: any;
  polygonControllers: any;
  cityBoundary: any;
  constructor(props: Props) {
    super(props);
    this.state = {
      center: undefined,
      isDrawable: false,
      mapCleared: false,
      mapReady: false,
      polygonCoordinates: '',
      showMarkers: true,
    };
  }

  componentDidMount() {
    EventEmitter.on('onMouseOverListing', this.handleMouseHoverEvent);
    EventEmitter.on('onMouseLeaveListing', this.handleMouseLeaveEvent);
    EventEmitter.on('showMarker', this.handleMarkerDisplayHelper);
    EventEmitter.on('onSlideChange', this.onslideChangeHelper);
    EventEmitter.on('highlightListing', this.highlightListing);
  }

  componentWillUnmount() {
    EventEmitter.removeListener(
      'onMouseOverListing',
      this.handleMouseHoverEvent,
    );
    EventEmitter.removeListener(
      'onMouseLeaveListing',
      this.handleMouseLeaveEvent,
    );
    EventEmitter.removeListener('showMarker', this.handleMarkerDisplayHelper);
    EventEmitter.removeListener('onSlideChange', this.onslideChangeHelper);
    EventEmitter.removeListener('highlightListing', this.highlightListing);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.initialMapLoad && !prevProps.initialMapLoad) {
      // Clear existing polygons and custom drawn boundaries
      this.setState({
        mapCleared: true,
        polygonCoordinates: '',
        showMarkers: false,
      });
      this.clearCityBoundary();
      if (this.polygonControllers) {
        this.polygonControllers.setVisible(false);
      }
      // Using setTimeout so that we get the new coords on location change
      setTimeout(() => {
        this.getMapBoundsManually(this.mapView.map_);
      });
    } else if (
      this.props.placeBoundaries &&
      JSON.stringify(prevProps.placeBoundaries) !==
        JSON.stringify(this.props.placeBoundaries)
    ) {
      // Render new city boundaries
      this.frameCityBoundary(this.props.placeBoundaries);
    }
  }

  frameCityBoundary = (placeBoundaries: any) => {
    this.cityBoundary = placeBoundaries.map((placeBoundary: any) => {
      return new this.mapView.maps_.Polygon({
        clickable: false,
        fillColor: '#463264',
        fillOpacity: 0.25,
        geodesic: true,
        map: this.mapView.map_,
        paths: placeBoundary,
        strokeColor: '#463264',
        strokeWeight: 3,
      });
    });
    // Logic to find the map bounds when showing polygons for a place
    let latLngBounds = new google.maps.LatLngBounds();
    for (var i = 0; i < placeBoundaries.length; i++) {
      for (var j = 0; j < placeBoundaries[i].length; j++) {
        latLngBounds.extend(
          new google.maps.LatLng(
            placeBoundaries[i][j].lat,
            placeBoundaries[i][j].lng,
          ),
        );
      }
    }
    // Fit Bounds for Polygons on google Map
    this.mapView.map_.fitBounds(latLngBounds);
  };

  onMapLoaded = () => {
    if (this.props.initialMapLoad) {
      this.getMapBoundsManually(this.mapView.map_);
    } else if (
      this.props.placeBoundaries &&
      !this.state.mapCleared &&
      this.cityBoundary === undefined
    ) {
      this.frameCityBoundary(this.props.placeBoundaries);
    }
    if (!this.state.mapReady) {
      this.setState(
        {
          mapReady: true,
        },
        () => {
          this.mapView.maps_.event.addDomListener(
            this.mapView.googleMapDom_,
            'click',
            (e: any) => {
              if (!e.target.id) {
                this.onmapClick();
              }
            },
          );
        },
      );
    }
  };

  handleMarkerDisplayHelper = () => {
    this.setState({
      showMarkers: true,
    });
  };

  loadMapFeedHelper = (
    mapCenter: string,
    boundingBox: string,
    polyBoundaries?: string,
  ) => {
    EventEmitter.emit('onMapClick');
    const zoom = this.mapView.map_.zoom || 13;
    if (this.props.loadMapFeed && !this.state.isDrawable) {
      return this.props.loadMapFeed(
        mapCenter,
        boundingBox,
        polyBoundaries,
        true,
        zoom,
      );
    }
    return null;
  };

  handleMouseHoverEvent = (listing: any) => {
    this.props.focusMarker(listing.id);
  };

  handleMouseLeaveEvent = () => {
    this.props.onMarkerLeave();
  };

  handleSubmitEvent = () => {
    // Consolidated coordinates for the polygons
    this.setState({
      mapCleared: false,
      showMarkers: false,
    });
    const polyBoundaries = this.state.polygonCoordinates;
    const mapCenter = `${this.mapView.map_
      .getCenter()
      .lat()},${this.mapView.map_.getCenter().lng()}`;
    const northEast = this.mapView.map_.getBounds().getNorthEast();
    const southWest = this.mapView.map_.getBounds().getSouthWest();
    const boundingBox = `${northEast.lat()},${northEast.lng()},${southWest.lat()},${southWest.lng()}`;
    this.loadMapFeedHelper(mapCenter, boundingBox, polyBoundaries);
  };

  handleDrawEvent = () => {
    // Invoked when User clicks DRAW button
    this.enableDrawableHelper();
  };

  clearCityBoundary = () => {
    this.cityBoundary &&
      this.cityBoundary.map((cityBoundary: any) => {
        cityBoundary.setVisible(false);
      });
    this.cityBoundary = undefined;
  };

  handleResetDrawEvent = () => {
    this.setState({
      mapCleared: false,
      polygonCoordinates: '',
    });
    if (this.polygonControllers) {
      this.polygonControllers.setVisible(false);
    }
    if (this.cityBoundary) {
      this.clearCityBoundary();
      this.props.toggleCityBoundary && this.props.toggleCityBoundary(false);
    }
    this.mapView.maps_.event.addDomListener(
      this.mapView.googleMapDom_,
      'mousedown',
      this.drawFreeHand,
    );
  };

  handleClearMap = () => {
    this.setState(
      {
        mapCleared: true,
        polygonCoordinates: '',
        showMarkers: false,
      },
      () => {
        if (this.polygonControllers) {
          this.polygonControllers.setVisible(false);
        }
        if (this.cityBoundary) {
          this.clearCityBoundary();
          this.props.toggleCityBoundary && this.props.toggleCityBoundary(false);
        }
        this.getMapBoundsManually(this.mapView.map_);
      },
    );
  };

  enableDrawableHelper = () => {
    // Clear the old Listeners if exits and attach a new 'mousedown' listener on Map
    if (this.mapView) {
      this.mapView.maps_.event.clearListeners(
        this.mapView.googleMapDom_,
        'mousedown',
      );
      this.mapView.maps_.event.addDomListener(
        this.mapView.googleMapDom_,
        'mousedown',
        this.drawFreeHand,
      );
    }
  };

  drawFreeHand = () => {
    const poly = new this.mapView.maps_.Polyline({
      clickable: false,
      map: this.mapView.map_,
      strokeColor: '#463264',
      strokeWeight: 3,
    });
    // Added mousemove listener to track the user's mouse movement
    const move = this.mapView.maps_.event.addListener(
      this.mapView.map_,
      'mousemove',
      (e: any) => {
        poly.getPath().push(e.latLng);
      },
    );

    // Added mouseup listener to check when the user releases the mouse button
    this.mapView.maps_.event.addListenerOnce(
      this.mapView.map_,
      'mouseup',
      (e: any) => {
        this.mapView.maps_.event.removeListener(move);
        const path = poly.getPath();
        poly.setMap(null);
        const polygon = new this.mapView.maps_.Polygon({
          clickable: false,
          fillColor: '#463264',
          fillOpacity: 0.25,
          geodesic: true,
          map: this.mapView.map_,
          path,
          strokeColor: '#463264',
          strokeWeight: 3,
        });
        this.polyComplete(polygon);
        this.mapView.maps_.event.clearListeners(
          this.mapView.googleMapDom_,
          'mousedown',
        );
      },
    );
  };

  // Helper Method to get the coordinates across the polygon line
  polyComplete = (poly: any) => {
    this.polygonControllers = poly;
    let bounds = '';
    const paths = poly.getPaths();
    paths.forEach((path: any) => {
      const ar = path.getArray();
      for (let i = 0, l = ar.length; i < l; i++) {
        const lat = ar[i].lat();
        const lng = ar[i].lng();
        bounds += `${lng} ${lat},`;
      }
      if (ar[0]) {
        bounds += `${ar[0].lng()} ${ar[0].lat()}`;
      }
    });
    this.setState({
      polygonCoordinates: bounds,
    });
  };

  getMapBounds = (mapBounds: any) => {
    if (this.props.initialMapLoad) {
      const { bounds, center } = mapBounds;
      const boundingBox = `${bounds.ne.lat},${bounds.ne.lng},${bounds.sw.lat},${bounds.sw.lng}`;
      const mapCenter = `${center.lat},${center.lng}`;
      this.loadMapFeedHelper(mapCenter, boundingBox);
    }
  };

  getMapBoundsManually = async (map: any) => {
    // Get the Map Bound area
    const centerCordinate = await map.getCenter();
    const mapCenter = `${centerCordinate.lat()},${centerCordinate.lng()}`;
    const northEast = map.getBounds().getNorthEast();
    const southWest = map.getBounds().getSouthWest();
    const boundingBox = `${northEast.lat()},${northEast.lng()},${southWest.lat()},${southWest.lng()}`;
    // If user drew on the map use those boundaries
    const polyBoundaries = this.state.polygonCoordinates
      ? this.state.polygonCoordinates
      : undefined;

    this.loadMapFeedHelper(mapCenter, boundingBox, polyBoundaries);
  };

  onMouseOverMarker = (listing: any) => {
    this.props.onMarkerEnter(
      listing.name,
      listing.latitude,
      listing.longitude,
      listing,
      listing.key,
      listing.id,
    );
  };

  onMouseLeaveMarker = () => {
    this.props.onMarkerLeave();
  };
  onmapClick = () => {
    this.props.markerReset();
    EventEmitter.emit('onMapClick');
  };

  onslideChangeHelper = (listing: any) => {
    this.props.onMarkerClick(listing, true);
  };

  onMarkerClickHelper = (listing?: any) => {
    EventEmitter.emit('onMouseLeaveMarker');
    this.props.onMarkerClick(listing, isMobile() ? true : false);
    EventEmitter.emit('onMarkerClicked', listing);
  };

  highlightListing = ({ selectedListingId }: any) => {
    this.props.onMarkerClick({ id: selectedListingId }, true);
    EventEmitter.emit('onMarkerClicked', { id: selectedListingId });
  };

  generateCustomMarker = (listing: any, favorited: boolean) => {
    const price = formatShortPrice(listing.listPrice);
    let marker =
      this.props.markerClicked === listing.id ? (
        <GreenMarker content={price} />
      ) : this.props.markerFocused === listing.id ? (
        <DarkBlueMarker content={price} />
      ) : (
        <BlueMarker content={price} />
      );
    if (favorited) {
      marker = <FavoriteMarker />;
    }

    return (
      <CustomMarker
        isOverlayShown={this.props.isOverlayShown}
        key={listing.id}
        lat={listing.latitude}
        listing={listing}
        lng={listing.longitude}
        marker={marker}
        markerClicked={this.props.markerClicked}
        markerFocused={this.props.markerFocused}
        onMarkerClick={this.onMarkerClickHelper}
        onMouseLeaveMarker={this.onMouseLeaveMarker}
        onMouseOverMarker={this.onMouseOverMarker}
        overlayListing={this.props.overlayListing}
        router={this.props.router}
        selectedListing={this.props.selectedListing}
      />
    );
  };

  generateClusterMarker = (listings: any) => {
    let listingIds = '';
    listings.forEach((listing: any) => {
      listingIds += `${listing.id},`;
    });
    const marker =
      listingIds.includes(this.props.markerClicked) &&
      this.props.markerClicked.length !== 0 ? (
        <GreenMarker content={`${listings.length} Units`} />
      ) : listingIds.includes(this.props.markerFocused) &&
        this.props.markerFocused.length !== 0 ? (
        <DarkBlueMarker content={`${listings.length} Units`} />
      ) : (
        <BlueMarker content={`${listings.length} Units`} />
      );

    return (
      <ClusterMarker
        isOverlayShown={this.props.isOverlayShown}
        key={makeId()}
        listing={listings[0]}
        listings={listings}
        lat={listings[0].latitude}
        lng={listings[0].longitude}
        marker={marker}
        markerClicked={this.props.markerClicked}
        markerFocused={this.props.markerFocused}
        onMarkerClick={this.onMarkerClickHelper}
        onMouseLeaveMarker={this.onMouseLeaveMarker}
        onMouseOverMarker={this.onMouseOverMarker}
        overlayListing={this.props.overlayListing}
        selectedListing={this.props.selectedListing}
        router={this.props.router}
      />
    );
  };

  generateMarkers = () => {
    const { groupedResults } = this.props;
    const markers: any = [];
    if (Object.entries(groupedResults).length !== 0) {
      for (const item in groupedResults) {
        if (groupedResults[item].length > 1) {
          markers.push(this.generateClusterMarker(groupedResults[item]));
        } else {
          const listing = groupedResults[item][0];
          let favorited = false;
          if (this.props.favorites && this.props.favorites.length > 0) {
            favorited = this.props.favorites.find((favorite: any) => {
              return favorite.id === listing.id;
            });
          }
          markers.push(this.generateCustomMarker(listing, favorited));
        }
      }
    }
    return markers;
  };

  handleDrawable = () => {
    this.setState(
      {
        isDrawable: !this.state.isDrawable,
      },
      () => {
        if (this.state.isDrawable) {
          this.handleResetDrawEvent();
          this.handleDrawEvent();
        } else {
          this.handleSubmitEvent();
        }
      },
    );
  };

  handleCancelDrawable = () => {
    this.setState(
      {
        isDrawable: false,
      },
      () => {
        this.handleClearMap();
      },
    );
  };

  render() {
    const { feed, feedFetched } = this.props;
    const displayListingCount =
      feed[0] && feed[0].fullCount > FEED_FETCH_LIMIT
        ? `${feed.length} of ${normalizePrice(feed[0].fullCount)}`
        : `${feed.length}`;
    return (
      <div style={{ height: '100%', width: '100%' }}>
        {this.state.mapReady &&
          this.props.loadMapFeed &&
          !this.state.isDrawable &&
          displayListingCount && (
            <div className={styles.houseCountContainer}>
              <div className={styles.houseCount}>
                <span style={{ padding: '0 20px' }}>
                  {displayListingCount} Results
                </span>
              </div>
            </div>
          )}
        {this.state.mapReady && (
          <React.Fragment>
            {!this.state.isDrawable && (
              <div
                className={
                  this.state.polygonCoordinates !== ''
                    ? `${styles.drawButtonContainer} ${styles.compress}`
                    : styles.drawButtonContainer
                }
              >
                {this.state.polygonCoordinates !== '' || this.cityBoundary ? (
                  <Button
                    variant="contained"
                    size="small"
                    className={styles.clearButton}
                    onClick={this.handleClearMap}
                  >
                    <div className={styles.drawContainer}>
                      <X size={24} color="#979797" weight="duotone" />
                      <span className={styles.drawText}>Clear</span>
                    </div>
                  </Button>
                ) : this.props.allowDrawing ? (
                  // Allow drawing only on web for now
                  <Button
                    variant="contained"
                    size="small"
                    className={styles.drawOnMapButton}
                    onClick={this.handleDrawable}
                    disabled={
                      this.state.isDrawable &&
                      this.state.polygonCoordinates === '' &&
                      true
                    }
                  >
                    <div className={styles.drawContainer}>
                      <Pencil size={24} color="#979797" weight="duotone" />
                      <span className={styles.drawText}>Draw</span>
                    </div>
                  </Button>
                ) : (
                  <div style={{ margin: 10 }} />
                )}
                {feedFetched && (
                  <div
                    className={
                      this.state.polygonCoordinates !== ''
                        ? `${styles.saveButtonContainer} ${styles.compress}`
                        : styles.saveButtonContainer
                    }
                  >
                    <SaveSearchButton webButton />
                  </div>
                )}
              </div>
            )}
            {this.state.isDrawable && (
              <React.Fragment>
                <div className={`row ${styles.buttonContainer} mobile-only`}>
                  <Button
                    style={{ padding: 0 }}
                    onClick={() => {
                      if (this.state.polygonCoordinates === '') {
                        this.handleCancelDrawable();
                      } else {
                        this.handleResetDrawEvent();
                      }
                    }}
                  >
                    <div className={styles.mapButtons}>
                      {this.state.polygonCoordinates === ''
                        ? 'CANCEL'
                        : 'RESET'}
                    </div>
                  </Button>
                  <p>Draw on Map</p>
                  <Button
                    style={{ padding: 0 }}
                    onClick={this.handleDrawable}
                    disabled={
                      this.state.isDrawable &&
                      this.state.polygonCoordinates === '' &&
                      true
                    }
                  >
                    <div className={styles.mapButtons}>APPLY</div>
                  </Button>
                </div>
                <div className={`${styles.buttonContainer} no-mobile`}>
                  <div style={{ paddingLeft: 25 }}>
                    <p>Draw a shape to search a specific area</p>
                  </div>
                  <div>
                    <Button
                      style={{ padding: 0 }}
                      onClick={() => {
                        if (this.state.polygonCoordinates === '') {
                          this.handleCancelDrawable();
                        } else {
                          this.handleResetDrawEvent();
                        }
                      }}
                    >
                      <div className={styles.mapButtons}>
                        {this.state.polygonCoordinates === ''
                          ? 'CANCEL'
                          : 'RESET'}
                      </div>
                    </Button>
                    <Button
                      style={{ padding: 0 }}
                      onClick={this.handleDrawable}
                      disabled={
                        this.state.isDrawable &&
                        this.state.polygonCoordinates === '' &&
                        true
                      }
                    >
                      <div className={styles.mapButtons}>APPLY</div>
                    </Button>
                  </div>
                </div>
              </React.Fragment>
            )}
          </React.Fragment>
        )}
        <GoogleMapReact
          bootstrapURLKeys={{
            key: config.googleMaps.apiKey,
            libraries: config.googleMaps.libraries,
          }}
          center={this.props.center}
          zoom={this.props.zoom}
          draggable={!this.state.isDrawable}
          onDragEnd={this.getMapBoundsManually}
          onZoomAnimationEnd={() => {
            this.getMapBoundsManually(this.mapView.map_);
          }}
          onTilesLoaded={this.onMapLoaded}
          options={{
            clickableIcons: false,
            controlSize: 30,
            disableDoubleClickZoom: false,
            draggableCursor: this.state.isDrawable
              ? 'url(/static/img/pen_cursor.png) -100 100, crosshair'
              : '',
            fullscreenControl: false,
            gestureHandling: 'greedy',
            keyboardShortcuts: false,
            panControl: false,
            scrollwheel: true,
            zoomControl: isWeb() ? true : false,
          }}
          ref={(ref: any) => (this.mapView = ref)}
          shouldUnregisterMapOnUnmount
          yesIWantToUseGoogleMapApiInternals
        >
          {this.state.showMarkers &&
            this.state.mapReady &&
            !this.state.isDrawable &&
            this.generateMarkers()}
        </GoogleMapReact>
      </div>
    );
  }
}

const mapStateToProps = (state: any) => ({
  feedFetched: state.feed.feedFetched,
});

export default connect(mapStateToProps)(GoogleMapContainer);
